
import { Menu, X, UserCircle, Group, History, LogOut, Settings, Archive, BookOpen, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { EditProfileDialog } from "@/components/users/EditProfileDialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useTranslation } from 'react-i18next';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [archivedGroups, setArchivedGroups] = useState<{
    id: string;
    name: string;
  }[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const isGroupPage = location.pathname.includes('/group/');

  const fetchUserData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      
      if (error) throw error;
      
      // Return early if profile doesn't exist
      if (!profile) {
        console.error('No profile found for user');
        return;
      }

      // Use profile data with fallbacks
      const displayName = profile.name || profile.username || session.user.email?.split('@')[0] || t('common.anonymous');
      const avatarUrl = profile.avatar_url || 'https://api.dicebear.com/7.x/adventurer/svg?seed=default';
      
      setUser({
        ...session.user,
        ...profile,
        name: displayName,
        avatar_url: avatarUrl
      });

    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const fetchArchivedGroups = async () => {
    try {
      const { data: {
        session
      } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const {
        data,
        error
      } = await supabase.from('groups').select(`
          id,
          name,
          group_members!inner(profile_id)
        `).eq('status', 'archived').eq('group_members.profile_id', session.user.id);
      if (error) {
        console.error('Error fetching archived groups:', error);
        return;
      }
      setArchivedGroups(data.map(group => ({
        id: group.id,
        name: group.name
      })));
    } catch (error) {
      console.error('Error fetching archived groups:', error);
    }
  };

  useEffect(() => {
    fetchUserData();
    fetchArchivedGroups();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      setIsOpen(false);
      localStorage.clear();
      sessionStorage.clear();
      document.cookie = 'sb-access-token=; Max-Age=0; path=/; domain=' + window.location.hostname;
      document.cookie = 'sb-refresh-token=; Max-Age=0; path=/; domain=' + window.location.hostname;
      await supabase.auth.signOut();
      window.location.href = '/login';
    } catch (error) {
      console.error("Logout error:", error);
      window.location.href = '/login';
    }
  }, []);

  const handleNavigation = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  return <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center">
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="mr-2">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[300px] p-0" onInteractOutside={() => setIsOpen(false)} onEscapeKeyDown={() => setIsOpen(false)}>
            <div className="flex flex-col h-full">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gradient">
                    {t('common.menu')}
                  </h2>
                </div>
                {user && <div className="flex flex-col items-center space-y-2 p-3 rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-green/10 border border-primary/10">
                    <div className="relative">
                      <Avatar className="h-16 w-16 ring-2 ring-background">
                        <AvatarImage src={user.avatar_url} />
                        <AvatarFallback className="bg-gradient-to-r from-primary/20 to-green/20 text-lg">
                          {user.name?.[0] || user.username?.[0] || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <Badge variant="gradient" className="absolute -bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#e4ffe4] text-xs py-0">
                        {t('common.activeNow')}
                      </Badge>
                    </div>
                    <div className="text-center">
                      <h3 className="text-md font-semibold">{user.name || t('common.anonymous')}</h3>
                      <p className="text-xs text-muted-foreground">@{user.username || 'username'}</p>
                    </div>
                    <EditProfileDialog user={user} onProfileUpdate={fetchUserData} />
                  </div>}
              </div>
              <nav className="flex-1 overflow-auto py-3">
                <div className="space-y-1 px-3">
                  <Button variant="ghost" className="w-full justify-start h-12 rounded-xl font-medium text-base hover:bg-primary/10" onClick={() => handleNavigation("/app")}>
                    <Group className="mr-3 h-5 w-5 text-primary" />
                    {t('groups.title')}
                  </Button>
                  
                  <Button variant="ghost" className="w-full justify-start h-12 rounded-xl font-medium text-base hover:bg-primary/10" onClick={() => handleNavigation("/blog")}>
                    <BookOpen className="mr-3 h-5 w-5 text-primary" />
                    {t('blog.title') || "Blog"}
                  </Button>
                  
                  {user && user.role === 'admin' && (
                    <Button variant="ghost" className="w-full justify-start h-12 rounded-xl font-medium text-base hover:bg-primary/10" onClick={() => handleNavigation("/admin/blog")}>
                      <LayoutDashboard className="mr-3 h-5 w-5 text-primary" />
                      {t('blog.admin.title') || "Blog Admin"}
                    </Button>
                  )}
                  
                  <Collapsible open={showArchived} onOpenChange={setShowArchived} className="w-full">
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" className="w-full justify-between h-12 rounded-xl font-medium text-base hover:bg-primary/10">
                        <div className="flex items-center">
                          <Archive className="mr-3 h-5 w-5 text-primary" />
                          {t('groups.archivedGroups')}
                        </div>
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className={`transition-transform ${showArchived ? "rotate-180" : ""}`}>
                          <path d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z" fill="currentColor" />
                        </svg>
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="overflow-hidden pl-10 pr-3">
                      <div className="pl-2 mt-2 space-y-1">
                        {archivedGroups.length === 0 ? <p className="text-sm text-muted-foreground py-2 px-1">
                            {t('groups.noArchivedGroups') || "No archived groups"}
                          </p> : archivedGroups.map(group => <Button key={group.id} variant="ghost" size="sm" className="w-full justify-start text-sm font-normal" onClick={() => handleNavigation(`/group/${group.id}`)}>
                              <span className="truncate">{group.name}</span>
                            </Button>)}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <Button variant="ghost" className="w-full justify-start h-12 rounded-xl font-medium text-base hover:bg-primary/10" onClick={() => handleNavigation("/settings/payment-methods")}>
                    <Settings className="mr-3 h-5 w-5 text-primary" />
                    {t('payments.methods')}
                  </Button>
                </div>
              </nav>
              <div className="p-4 border-t border-primary/10">
                <Button variant="ghost" className="w-full justify-start h-12 rounded-xl font-medium text-base text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950" onClick={handleLogout}>
                  <LogOut className="mr-3 h-5 w-5" />
                  {t('auth.logout')}
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
        
        {/* Center logo with absolute positioning to ensure perfect centering */}
        <div className="absolute left-0 right-0 mx-auto flex justify-center items-center pointer-events-none">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/app")} 
            className="p-0 pointer-events-auto"
          >
            <img 
              src="https://tkrrgpbntaqiupgcayjm.supabase.co/storage/v1/object/public/Logo//SakuWari_logo_transparent_black.png" 
              alt="SakuWari Logo" 
              className="h-7 dark:invert" 
            />
          </Button>
        </div>
        
        {/* Add spacer div to push language switcher to the right */}
        <div className="flex-1"></div>
        <LanguageSwitcher />
      </div>
    </header>;
};
